<template>
  <div class="mt-10">
    <div class="api-title">
      {{ $t('api.yourTokens') }}
    </div>
    <v-data-table
      :items="tokens"
      :headers="headers"
      :loading="loading.mainTable"
      :no-data-text="$t('api.noTokens')"
      :options.sync="options"
      :server-items-length="totalTokens"
      :hide-default-footer="totalTokens === 0"
      :footer-props="{
        'items-per-page-options': [10, 25, 50]
      }"
      :item-class="tableItemClass"
    >
      <template #[`item.name`]="{ item }">
        <span class="api-table-name">
          {{ item.name }}

          <div
            v-if="isExpired(item.expiresAt)"
            style="color: #FF5252; font-size: 12px;"
          >
            {{ $t('api.expired') }}
          </div>
        </span>
      </template>

      <template #[`item.created_at`]="{ item }">
        {{ formatDate(item.createdAt) }}
      </template>

      <template #[`item.expires_at`]="{ item }">
        {{ formatDate(item.expiresAt) }}
      </template>

      <template #[`item.last_used_at`]="{ item }">
        {{ item.lastUsedAt ? formatDate(item.lastUsedAt) : $t('api.neverUsed') }}
      </template>

      <template #[`item.delete`]="{ item }">
        <v-menu
          open-on-click
          top
          offset-y
          offset-x
          nudge-left="71"
          nudge-top="10"
          content-class="api-delete-menu"
        >
          <template #activator="{ on, attrs, value }">
            <svg
              width="14"
              height="19"
              viewBox="0 0 14 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              v-bind="attrs"
              style="vertical-align: middle;"
              @mouseover="apiDeleteMenuHover = item.id"
              @mouseleave="apiDeleteMenuHover = null"
              v-on="on"
            >
              <bin-icon-filled v-if="value || apiDeleteMenuHover === item.id" />
              <bin-icon-outlined v-else />
            </svg>
          </template>

          <v-card elevation="1">
            <v-card-text class="pa-3">
              <div style="font-size: 14px;" class="d-flex align-center mb-2">
                <span style="font-weight: 600;">{{ $t('api.delete') + "?" }}</span>

                <v-btn icon small class="ml-2">
                  <svg
                    width="12" height="13" viewBox="0 0 12 13" fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.8337 1.97261L10.6587 0.797607L6.00033 5.45594L1.34199 0.797607L0.166992 1.97261L4.82533 6.63094L0.166992 11.2893L1.34199 12.4643L6.00033 7.80594L10.6587 12.4643L11.8337 11.2893L7.17533 6.63094L11.8337 1.97261Z" fill="#9D9D9D" />
                  </svg>
                </v-btn>
              </div>

              <v-btn
                color="primary"
                elevation="0"
                small
                rounded
                block
                :loading="deleteLoading"
                @click="deleteToken(item)"
              >
                {{ $t('api.yes') }}
              </v-btn>
            </v-card-text>
          </v-card>
        </v-menu>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from 'vuex'
import { mapFields } from "vuex-map-fields";
import BinIconOutlined from "@/components/icons/BinIconOutlined.vue";
import BinIconFilled from "@/components/icons/BinIconFilled.vue";

export default {
  name: 'ApiTable',

  components: {
    BinIconOutlined,
    BinIconFilled
  },

  data() {
    return {
      apiDeleteMenuHover: null,
      deleteLoading: false,
    }
  },

  computed: {
    ...mapGetters({
      tokens: 'api/tokens',
      totalTokens: 'api/totalTokens',
      loading: 'api/loading',
    }),
    ...mapFields('api', ['options']),

    headers() {
      return [
        { text: this.$t('common.name'), value: 'name', width: 300 },
        { text: this.$t('api.сreated'), value: 'created_at', width: 120 },
        { text: this.$t('api.expiration2'), value: 'expires_at', width: 130 },
        { text: this.$t('api.lastUsed'), value: 'last_used_at', width: 200 },
        { text: '', value: 'delete', sortable: false, align: 'end' },
      ]
    }
  },

  watch: {
    options: {
      deep: true,
      handler (newVal, oldVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.$store.dispatch('api/loadTokens')
        }
      },
    },
  },

  created() {
    this.$store.dispatch('api/loadTokens')
  },

  methods: {
    tableItemClass({ expiresAt }) {
      if (this.isExpired(expiresAt)) {
        return 'api-table-item-expired'
      }

      return ''
    },
    formatDate(date) {
      return moment(date).format('DD.MM.YYYY')
    },

    isExpired(date) {
      return moment(date).local().isBefore(moment())
    },

    async deleteToken(item) {
      try {
        this.deleteLoading = true

        await this.api.delete(`/personal-access-tokens/${item.id}`)

        this.$store.dispatch('api/loadTokens')
      } catch (error) {
        // do not nothing
      } finally {
        this.deleteLoading = false
      }
    }
  }
}
</script>

<style scoped>
.api-title {
  font-weight: 500;
  font-size: 14px;
  color: #212121;
}
.api-container {
  display: flex;
  align-items: center;
  background: #F2F2F2;
  border-radius: 8px;
  padding: 12px;
  margin-top: 8px;
}
.api-table-name {
  display: inline-block;
  font-weight: 500;
  width: 300px;
  padding: 12px 0;
}
</style>

<style>
.api-delete-menu {
  box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
}
.api-table-item-expired {
  color: #ACACAC;
}
.api-table-item-expired td:nth-child(3){
  color: #FF5252;
}
</style>
